import * as Sentry from '@sentry/vue'
import { isScoredSlideType } from '@ahaslides-product/plugins-api'

export const EMBED_APP_FLAG = 'AHA-42142-embed-app'

const SET_MANIFESTS = 'SET_MANIFESTS'
const SET_FULL_SCREEN = 'SET_FULL_SCREEN'

const _state = {
  manifests: [],
  fullScreen: false,
}

// Manifest assets (icon, previewUrl) may be specified as a path relative to
// the plugin's baseUrl. Resolve them so consumers always see an absolute URL.
const _resolveAssetUrl = (asset, baseUrl) => {
  if (typeof asset !== 'string' || asset === '') return asset
  if (asset.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(asset)) return asset
  if (typeof baseUrl !== 'string' || baseUrl === '') return asset
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const path = asset.startsWith('/') ? asset : `/${asset}`
  return `${base}${path}`
}

const _getters = {
  isEnabled: (_s, _g, _rs, rootGetters) => {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    return !!variant?.enabled
  },
  manifests: (state) => state.manifests,
  isFullScreen: (state, getters) => getters.isEnabled && state.fullScreen,
  manifestById: (state) => (id) => state.manifests.find((m) => m.id === id),
  appsByContext: (state) => (context) => {
    const tabs = []
    state.manifests.forEach((app) => {
      (app.staticTabs || []).forEach((tab) => {
        if (tab.context === context) {
          tabs.push({
            ...tab,
            appId: app.id,
            name: tab.name || app.name,
            icon: tab.icon || app.icon,
            app,
          })
        }
      })
    })
    return tabs
  },
  customSlideTypes: (state) => {
    const types = []
    state.manifests.forEach((app) => {
      (app.slideTypes || []).forEach((slideType) => {
        if (slideType && slideType.type) {
          types.push({
            ...slideType,
            icon: _resolveAssetUrl(slideType.icon, app.baseUrl),
            previewUrl: _resolveAssetUrl(slideType.previewUrl, app.baseUrl),
            appId: app.id,
            app,
          })
        }
      })
    })
    return types
  },
  isScoredSlide: (_, getters) => (slide) => {
    if (!slide) return false
    return isScoredSlideType(getters.customSlideTypeByType(slide.type))
  },

  customSlideTypeByType: (_, getters, __, rootGetters) => (type) => {
    if (!type) return null
    const found = getters.customSlideTypes.find((s) => s.type === type)
    if (found) return found

    if (rootGetters && rootGetters['featureToggles/canUseNewSlideMarketPlace']) {
      const marketplaceSlideTypes = rootGetters['slideMarketplace/marketplaceSlideTypes']
      if (marketplaceSlideTypes) {
        const marketplaceSlide = marketplaceSlideTypes.find((s) => s.type === type && (s.type.startsWith('marketplace/') || s.source === 'market'))
        if (marketplaceSlide) {
          return {
            ...marketplaceSlide,
            plugin: true,
            setting: {
              enableQuestionTitle: false,
              enableQuestionDescription: false,
              enableTimeLimit: false,
              enableStopSubmitssionSetting: false,
              enableHideResultSetting: false,
              enableQuestionImage: false,
              enableVoteCount: false,
              enableLabelOtherSetting: false,
              enableMultipleSubmission: false,
              enableFullScreen: false,
              ...(marketplaceSlide.setting || {}),
            },
          }
        }
      }
    }
    return null
  },
}

// Optional per-env override for the aha-workspace app's baseUrl, so a sandbox
// or local build can point the workspace iframe at a specific host (e.g. the
// dev workspace app) without needing an Unleash payload edit. Empty / unset
// leaves the manifest untouched.
const _workspaceAppUrlOverride = () => {
  const raw = process.env.VUE_APP_WORKSPACE_APP_URL
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null
}

const _applyWorkspaceUrlOverride = (manifests) => {
  const override = _workspaceAppUrlOverride()
  if (!override) return manifests
  return manifests.map((m) => (m?.id === 'aha-workspace' ? { ...m, baseUrl: override } : m))
}

const _parseManifests = (payload) => {
  if (!payload?.value) return []
  try {
    const parsed = typeof payload.value === 'string' ? JSON.parse(payload.value) : payload.value
    if (!Array.isArray(parsed)) return []
    const cleaned = parsed.filter((item) => item && typeof item === 'object')
    return _applyWorkspaceUrlOverride(cleaned)
  } catch (error) {
    Sentry.captureException(error, { tags: { source: 'embed-app.module.js' } })
    return []
  }
}

const actions = {
  loadManifests({ commit, rootGetters }) {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    if (!variant?.enabled) {
      commit(SET_MANIFESTS, [])
      return
    }

    const manifests = _parseManifests(variant.payload)
    commit(SET_MANIFESTS, manifests)
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_MANIFESTS](state, manifests) {
    state.manifests = manifests
  },
  [SET_FULL_SCREEN](state, fullScreen) {
    state.fullScreen = fullScreen
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
